/**
 * Entity → display metadata mapping for AI search result cards.
 * Maps entity names (as returned by BTNet-Search API in snake_case)
 * to display labels and BDS icon components.
 *
 * Icon assignments follow the idealized Bp→BdsIcon mapping (icon-entity-mapping.csv).
 */
import type { ReactNode } from "react";

import type { TBdsIconProps } from "@buildertrend/components";
import {
    BdsIconAllowances,
    BdsIconBids,
    BdsIconBills,
    BdsIconChangeOrders,
    BdsIconClientUpdates,
    BdsIconComments,
    BdsIconCostCodes,
    BdsIconCreditMemos,
    BdsIconDailyLogs,
    BdsIconDocuments,
    BdsIconEstimate,
    BdsIconInternalUsers,
    BdsIconJobs,
    BdsIconLeadOpportunities,
    BdsIconLeadProposals,
    BdsIconLienWaivers,
    BdsIconMessages,
    BdsIconOwnerInvoices,
    BdsIconPeople,
    BdsIconPhotos,
    BdsIconPurchaseOrders,
    BdsIconReceipts,
    BdsIconRfIs,
    BdsIconSchedule,
    BdsIconSelections,
    BdsIconSpecifications,
    BdsIconStamp,
    BdsIconSubsVendors,
    BdsIconSurveys,
    BdsIconTimeClock,
    BdsIconToDos,
    BdsIconVideos,
    BdsIconWarranty,
} from "@buildertrend/components";

export interface IEntityMeta {
    label: string;
    icon: ReactNode;
}

// BdsIcon default size is "md" (20px) — no size prop needed.
function icon(IconComponent: React.ComponentType<TBdsIconProps>): ReactNode {
    return <IconComponent />;
}

/**
 * Keys are normalized entity names: lowercased with hyphens and underscores stripped.
 * The API returns snake_case (e.g. "daily_log") which normalizes to "dailylog".
 */
const META: Record<string, IEntityMeta> = {
    // ── Jobs & Leads ────────────────────────────────────────────────────
    job: { label: "Job", icon: icon(BdsIconJobs) },
    jobsite: { label: "Job", icon: icon(BdsIconJobs) },
    lead: { label: "Lead", icon: icon(BdsIconLeadOpportunities) },
    leadactivity: { label: "Lead Activity", icon: icon(BdsIconLeadOpportunities) },

    // ── People ──────────────────────────────────────────────────────────
    contact: { label: "Contact", icon: icon(BdsIconPeople) },
    sub: { label: "Sub / Vendor", icon: icon(BdsIconSubsVendors) },
    internaluser: { label: "Internal User", icon: icon(BdsIconInternalUsers) },

    // ── Scheduling ──────────────────────────────────────────────────────
    schedule: { label: "Schedule", icon: icon(BdsIconSchedule) },
    scheduleitem: { label: "Schedule", icon: icon(BdsIconSchedule) },
    phase: { label: "Phase", icon: icon(BdsIconSchedule) },

    // ── Tasks ───────────────────────────────────────────────────────────
    task: { label: "Task", icon: icon(BdsIconToDos) },

    // ── Selections ──────────────────────────────────────────────────────
    selection: { label: "Selection", icon: icon(BdsIconSelections) },
    selectionchoice: { label: "Selection Choice", icon: icon(BdsIconSelections) },
    selectionoption: { label: "Selection", icon: icon(BdsIconSelections) },

    // ── Financial ───────────────────────────────────────────────────────
    purchaseorder: { label: "Purchase Order", icon: icon(BdsIconPurchaseOrders) },
    changeorder: { label: "Change Order", icon: icon(BdsIconChangeOrders) },
    bill: { label: "Bill", icon: icon(BdsIconBills) },
    customerinvoice: { label: "Client Invoice", icon: icon(BdsIconOwnerInvoices) },
    allowance: { label: "Allowance", icon: icon(BdsIconAllowances) },
    costcode: { label: "Cost Code", icon: icon(BdsIconCostCodes) },
    costitem: { label: "Cost Item", icon: icon(BdsIconCostCodes) },
    deposit: { label: "Deposit", icon: icon(BdsIconOwnerInvoices) },
    creditmemo: { label: "Credit Memo", icon: icon(BdsIconCreditMemos) },
    estimate: { label: "Estimate", icon: icon(BdsIconEstimate) },
    lienwaiver: { label: "Lien Waiver", icon: icon(BdsIconLienWaivers) },
    receipt: { label: "Receipt", icon: icon(BdsIconReceipts) },

    // ── Bids ────────────────────────────────────────────────────────────
    bid: { label: "Bid", icon: icon(BdsIconBids) },
    bidpackage: { label: "Bid Package", icon: icon(BdsIconBids) },

    // ── Daily Logs & Warranty ───────────────────────────────────────────
    dailylog: { label: "Daily Log", icon: icon(BdsIconDailyLogs) },
    warranty: { label: "Warranty", icon: icon(BdsIconWarranty) },
    warrantyservice: { label: "Warranty Service", icon: icon(BdsIconWarranty) },

    // ── Media & Documents ───────────────────────────────────────────────
    document: { label: "Document", icon: icon(BdsIconDocuments) },
    photo: { label: "Photo", icon: icon(BdsIconPhotos) },
    video: { label: "Video", icon: icon(BdsIconVideos) },

    // ── Communication ───────────────────────────────────────────────────
    email: { label: "Message", icon: icon(BdsIconMessages) },
    emailmessage: { label: "Message", icon: icon(BdsIconMessages) },
    message: { label: "Message", icon: icon(BdsIconMessages) },
    channel: { label: "Channel", icon: icon(BdsIconMessages) },
    comment: { label: "Comment", icon: icon(BdsIconComments) },
    clientupdate: { label: "Client Update", icon: icon(BdsIconClientUpdates) },

    // ── Proposals ───────────────────────────────────────────────────────
    proposal: { label: "Proposal", icon: icon(BdsIconLeadProposals) },
    jobproposal: { label: "Proposal", icon: icon(BdsIconLeadProposals) },
    leadproposal: { label: "Proposal", icon: icon(BdsIconLeadProposals) },

    // ── RFIs & Submittals ───────────────────────────────────────────────
    rfi: { label: "RFI", icon: icon(BdsIconRfIs) },
    submittal: { label: "Submittal", icon: icon(BdsIconStamp) },
    specification: { label: "Specification", icon: icon(BdsIconSpecifications) },

    // ── Other ───────────────────────────────────────────────────────────
    survey: { label: "Survey", icon: icon(BdsIconSurveys) },
    timeclockentry: { label: "Time Clock Entry", icon: icon(BdsIconTimeClock) },
};

const DEFAULT_META: IEntityMeta = {
    label: "Item",
    icon: icon(BdsIconJobs),
};

/**
 * Normalize an entity name to a META lookup key.
 * The API returns snake_case (e.g. "daily_log"); strip separators to match META keys.
 */
function normalizeKey(entity: string): string {
    return entity.toLowerCase().replace(/[-_]/g, "");
}

/**
 * Stable identifier for a search hit used as the React `key` prop,
 * the `pendingKey` state value, and comparison target on the result card.
 */
function getHitKey(hit: { entity: string; id: string }): string {
    return `${hit.entity}:${hit.id}`;
}

export { getHitKey, normalizeKey };

export function getEntityMeta(entity: string): IEntityMeta {
    const key = normalizeKey(entity);
    if (META[key]) return META[key];

    // Generate a reasonable label from the raw entity name for unmapped entities
    const label = entity
        .toLowerCase()
        .replace(/[-_]/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (s) => s.toUpperCase());

    return { ...DEFAULT_META, label };
}
